import React, { Fragment, JSX, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router";
import { getStandardSlides } from "../../../api/slide.service";
import {
  AddStandardIcon,
  DeleteIcon,
  LinkIcon,
} from "../../../assets/icons/index";
import { ISlide } from "../../../model/interface/slide";
import DeleteModal from "../../shared/common/deleteModal";
import Tooltip from "../../shared/common/ellipsisText";
import SelectPageSize from "../../shared/common/pageSizeDropdown";
import Pagination from "../../shared/common/pagination";
import Loader from "../../shared/loader/loader";
import Constant from "../../../utils/constant";
import { XIcon } from "@heroicons/react/outline";
import {
  associateStandardSlides,
  deleteStandardSlide,
} from "../../../api/standard.service";
import { toast } from "react-toastify";
import { Modal } from "../../shared/common/Modal";

interface SlideProps {
  standardId: number;
  standardData: any;
}

const convertGoogleSlidesToEmbedUrl = (url: string): string => {
  const match = url.match(
    /https:\/\/docs\.google\.com\/presentation\/d\/([^/]+)/
  );
  if (!match || !match[1]) return url; // fallback if not matched
  const slideId = match[1];
  return `https://docs.google.com/presentation/d/${slideId}/embed`;
};

const SlideTableRow = ({
  slide,
  index,
  handleEditSlide,
  confirmDelete,
  setShowGoogleSlide,
  setCurrentSlideLink,
}) =>
  slide.isActive && (
    <tr
      key={slide.slideId}
      className={index % 2 === 0 ? "bg-white" : "bg-gray-100"}
    >
      <td className="relative px-3 py-2 text-sm font-normal text-left text-gray-500 capitalize">
        <a
          className="font-medium text-indigo-600 cursor-pointer hover:text-indigo-500 hover:underline"
          onClick={() => handleEditSlide(slide)}
        >
          {slide.slideId}
        </a>
        <span
          className={`mr-1 h-4/5 w-0.5 rounded-md inline-block absolute top-0 left-1.5 ${
            slide.isActive ? "bg-green-500" : "bg-red-500"
          }`}
        ></span>
      </td>
      <td className="px-3 py-2 text-sm font-normal text-left text-gray-500 capitalize">
        <a
          className="font-medium text-indigo-600 cursor-pointer hover:text-indigo-500 hover:underline"
          onClick={() => handleEditSlide(slide)}
        >
          {slide.title}
        </a>
      </td>
      <td className="px-3 py-2 text-sm font-normal text-left text-gray-500 capitalize">
        <a
          className="font-medium text-indigo-600 cursor-pointer hover:text-indigo-500 hover:underline"
          onClick={() => handleEditSlide(slide)}
        >
          <Tooltip text={slide.description} />
        </a>
      </td>
      <td className="px-3 py-2 text-sm font-normal text-left text-gray-500 capitalize">
        {slide.link && (
          <LinkIcon
            title={!slide.link ? "File not available" : "View Slide"}
            className={`w-5 mx-auto cursor-pointer ${
              !slide.link ? "text-red-500" : ""
            }`}
            onClick={() => {
              setCurrentSlideLink(convertGoogleSlidesToEmbedUrl(slide.link));
              setShowGoogleSlide(true);
            }}
          >
            {slide.link}
          </LinkIcon>
        )}
      </td>
      <td className="px-3 py-2 text-sm font-normal text-center text-gray-700">
        <div className="flex justify-center gap-3">
          {slide.isActive && (
            <DeleteIcon
              onClick={() => confirmDelete(slide.slideId)}
              className="w-4 text-xs font-light duration-300 ease-in-out cursor-pointer hover:text-red-500 transaction"
            />
          )}
        </div>
      </td>
    </tr>
  );

function Slide(props: SlideProps): JSX.Element {
  const defaultPageNumber = 1;
  const defaultPageSize = 10;
  const { standardId, standardData } = props;
  const navigate = useNavigate();
  const [slides, setSlides] = useState<ISlide[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [pageSize, setPageSize] = useState(defaultPageSize);
  const [pageNumber, setPageNumber] = useState(defaultPageNumber);
  const [totalPages, setTotalPages] = useState<number>(0);
  const [totalRecords, setTotalRecords] = useState<number>(0);
  const paginationRef: any = useRef(undefined);
  const pageSizeRef: any = useRef(undefined);
  const [slideDelete, setSlideDelete] = useState<number>(0);
  const [showDeletePopup, setShowDeletePopup] = useState<boolean>(false);
  const [showGoogleSlide, setShowGoogleSlide] = useState<boolean>(false);
  const [currentSlideLink, setCurrentSlideLink] = useState<string | null>(null);
  const [isLoaded, setIsLoaded] = useState<boolean>(false);
  const [showAssociateSlidePopup, setShowAssociateSlidePopup] =
    useState<boolean>(false);
  const [slideIds, setSlideIds] = useState<string>("");

  useEffect(() => {
    if (isLoaded) {
      bindSlides();
    }
  }, [pageNumber]);

  useEffect(() => {
    if (pageNumber === 1) {
      bindSlides();
      setIsLoaded(true);
    } else {
      paginationRef?.current?.onReset();
    }
  }, [pageSize]);

  const bindSlides = async () => {
    setLoading(true);
    const response = getStandardSlides(standardId, pageNumber, pageSize);
    const data: any = (await response).data;
    setLoading(false);
    setSlides(data.data);
    setTotalPages(Math.ceil(data.totalRecords / pageSize));
    setTotalRecords(data.totalRecords);
  };

  const deleteRecord = () => {
    deleteStandardSlide(props.standardId, slideDelete).then(() => {
      setShowDeletePopup(false);
      setSlideDelete(0);
      bindSlides();
    });
  };

  const closeDeletePopup = () => {
    setShowDeletePopup(false);
    setSlideDelete(0);
  };

  const confirmDelete = (idToDelete: number) => {
    if (idToDelete) {
      setShowDeletePopup(true);
      setSlideDelete(idToDelete);
    }
  };

  const handleAddSlideRedirect = () => {
    navigate(Constant.ADDSLIDE, { state: { autoFillData: standardData } });
  };

  const handleEditSlide = (slide: ISlide) => {
    navigate(Constant.SLIDEEDIT.replace(":id", slide.slideId.toString()));
  };

  const onSlideAssociate = async () => {
    const slideIdsArray = slideIds.split(",").map((id) => Number(id));
    await associateStandardSlides(standardId, slideIdsArray).then(
      (response) => {
        setShowAssociateSlidePopup(false);
        bindSlides();
        toast.info(response.data);
        setSlideIds("");
      }
    );
  };

  return (
    <Fragment>
      {loading && <Loader />}
      {showDeletePopup && (
        <DeleteModal
          closeDeleteModal={closeDeletePopup}
          deleteRecord={deleteRecord}
        />
      )}
      {showAssociateSlidePopup && (
        <div
          className="fixed z-10 inset-0 overflow-y-auto"
          aria-labelledby="modal-title"
          role="dialog"
          aria-modal="true"
        >
          <div className="flex items-end justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
            <div
              className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity"
              aria-hidden="true"
            ></div>

            <span
              className="hidden sm:inline-block sm:align-middle sm:h-screen"
              aria-hidden="true"
            >
              &#8203;
            </span>

            <div className="rounded-lg inline-block align-bottom bg-white px-4 pt-5 pb-4 text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full sm:p-6 h-auto">
              <div className="hidden sm:block absolute top-0 right-0 pt-4 pr-4">
                <button
                  onClick={() => setShowAssociateSlidePopup(false)}
                  type="button"
                  className="bg-white text-gray-400 hover:text-gray-500 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-text-primary"
                >
                  <XIcon className="h-6 w-6" aria-hidden="true" />
                </button>
              </div>

              <div className="flex border-b">
                <h2 className="text-gray-700 font-semibold text-xl">
                  Enter Slide ID(s)
                </h2>
              </div>

              <div className="mt-3 grid grid-cols-2">
                <input
                  value={slideIds}
                  onChange={(e) => setSlideIds(e.target.value)}
                  type="text"
                  autoComplete="off"
                  className=" mt-1 w-full block border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-primary-color focus:border-primary-color dark:focus:border-gray-500 dark:focus:ring-gray-500 dark:bg-custom-black dark:text-white sm:text-sm"
                />
              </div>
              <div className="flex pt-4">
                <button
                  type="button"
                  className="mt-4 inline-flex items-center text-sm text-white dark:text-white gap-2 bg-primary-color hover:bg-secondary-color py-1.5 px-4 rounded-lg focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-color"
                  onClick={() => onSlideAssociate()}
                >
                  Associate
                </button>
                <button
                  type="button"
                  className="mt-4 py-1.5 px-4 inline-flex ml-3 items-center text-sm rounded-lg text-gray-700 text-primary-color border border-gray-300 gap-2 bg-white hover:text-gray-500 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-color"
                  onClick={() => setShowAssociateSlidePopup(false)}
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
      {showGoogleSlide && (
        <Modal
          title={"View Slide"}
          setShowModal={setShowGoogleSlide}
          enableSubmitButton={false}
          contentType="slide"
        >
          <iframe
            src={currentSlideLink || ""}
            width="100%"
            height="480px"
            allowFullScreen
            title="Google Slides Presentation"
            className="rounded-lg"
          />
        </Modal>
      )}
      <div className="block px-4 py-4 text-sm text-gray-900 border-b border-gray-200 rounded-b-lg shadow">
        <div>
          <div className="relative flex">
            <div className="w-full">
              <div id="dataContainer">
                <div className="flex w-full mt-1 mb-4 rounded-lg">
                  <div className="flex justify-end space-x-4">
                    <button
                      onClick={handleAddSlideRedirect}
                      type="button"
                      className="flex items-center text-sm  cursor-pointer"
                    >
                      <AddStandardIcon />
                      Add Slide
                    </button>
                    <button
                      onClick={() => setShowAssociateSlidePopup(true)}
                      type="button"
                      className="ml-6 flex items-center text-sm  cursor-pointer"
                    >
                      <AddStandardIcon />
                      Associate Slide
                    </button>
                  </div>
                </div>
                <div>
                  <div className="overflow-hidden border-b border-gray-200 shadow sm:rounded-lg">
                    <table className="w-full divide-y divide-gray-200">
                      <thead className="bg-primary-color">
                        <tr>
                          <th
                            scope="col"
                            className="px-3 py-2 text-sm font-light tracking-wider text-left text-white dark:text-white"
                          >
                            Id
                          </th>
                          <th
                            scope="col"
                            className="px-3 py-2 text-sm font-light tracking-wider text-left text-white dark:text-white"
                          >
                            Title
                          </th>
                          <th
                            scope="col"
                            className="px-3 py-2 text-sm font-light tracking-wider text-left text-white dark:text-white"
                          >
                            Description
                          </th>
                          <th
                            scope="col"
                            className="px-3 py-2 text-sm font-light tracking-wider text-center text-white dark:text-white"
                          >
                            URL
                          </th>
                          <th
                            scope="col"
                            className="px-3 py-2 text-sm font-light tracking-wider text-center text-white dark:text-white"
                          >
                            Action
                          </th>
                        </tr>
                      </thead>
                      <tbody>
                        {slides?.map((slide, index) => (
                          <SlideTableRow
                            key={slide.slideId}
                            slide={slide}
                            index={index}
                            handleEditSlide={handleEditSlide}
                            confirmDelete={confirmDelete}
                            setShowGoogleSlide={setShowGoogleSlide}
                            setCurrentSlideLink={setCurrentSlideLink}
                          />
                        ))}
                      </tbody>
                    </table>
                  </div>

                  <div
                    className={`flex justify-between items-center ${
                      slides?.length === 0 ? "hidden" : ""
                    }`}
                  >
                    <Pagination
                      pages={totalPages}
                      setPageNumber={(newPageNumber) => {
                        if (pageNumber !== newPageNumber)
                          setPageNumber(newPageNumber);
                      }}
                      ref={paginationRef}
                    />
                    <span>
                      <span className="pr-4 ml-auto text-sm text-left-700">
                        Showing {pageNumber} to{" "}
                        {totalRecords < pageSize ? totalRecords : pageSize}{" "}
                        {"of "}
                        {totalRecords} results
                      </span>

                      <SelectPageSize
                        setPageSize={(size) => {
                          setPageSize(size);
                        }}
                        type={"second"}
                        ref={pageSizeRef}
                      />
                    </span>
                  </div>

                  {slides.length === 0 && (
                    <p className="p-2">No Records Found</p>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </Fragment>
  );
}

export { Slide };
